import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToolboxTalkBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState({});
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    content: '',
    duration_minutes: 10,
    key_points: [''],
    discussion_questions: ['']
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/toolbox-talks/categories`, { withCredentials: true });
      setCategories(res.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleKeyPointChange = (index, value) => {
    const newPoints = [...formData.key_points];
    newPoints[index] = value;
    setFormData(prev => ({ ...prev, key_points: newPoints }));
  };

  const addKeyPoint = () => {
    setFormData(prev => ({ ...prev, key_points: [...prev.key_points, ''] }));
  };

  const removeKeyPoint = (index) => {
    setFormData(prev => ({
      ...prev,
      key_points: prev.key_points.filter((_, i) => i !== index)
    }));
  };

  const handleQuestionChange = (index, value) => {
    const newQuestions = [...formData.discussion_questions];
    newQuestions[index] = value;
    setFormData(prev => ({ ...prev, discussion_questions: newQuestions }));
  };

  const addQuestion = () => {
    setFormData(prev => ({ ...prev, discussion_questions: [...prev.discussion_questions, ''] }));
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      discussion_questions: prev.discussion_questions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.content.trim()) {
      toast.error("Please enter the talk content");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/toolbox-talks`, {
        ...formData,
        key_points: formData.key_points.filter(p => p.trim()),
        discussion_questions: formData.discussion_questions.filter(q => q.trim())
      }, { withCredentials: true });
      
      toast.success("Toolbox talk created successfully!");
      navigate('/toolbox-talks');
    } catch (error) {
      console.error("Error creating talk:", error);
      toast.error(error.response?.data?.detail || "Failed to create toolbox talk");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="toolbox-talk-builder">
        {/* Header */}
        <div className="mb-8">
          <Link to="/toolbox-talks" className="inline-flex items-center text-muted-foreground hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Toolbox Talks
          </Link>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
            Create Toolbox Talk
          </h1>
          <p className="text-muted-foreground mt-1">
            Create a custom toolbox talk for your team.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Ladder Safety Refresher"
                  data-testid="talk-title-input"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                    <SelectTrigger data-testid="talk-category-select">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(categories).map(([key, name]) => (
                        <SelectItem key={key} value={key}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="5"
                    max="60"
                    value={formData.duration_minutes}
                    onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value))}
                    data-testid="talk-duration-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief description of this toolbox talk..."
                  data-testid="talk-description-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Talk Content</CardTitle>
            </CardHeader>
            <CardContent>
              <Label htmlFor="content">Main Content</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="Write the full content of your toolbox talk here..."
                className="min-h-[300px] mt-1.5"
                data-testid="talk-content-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Tip: Use line breaks to separate sections. You can include headers using **bold** text.
              </p>
            </CardContent>
          </Card>

          {/* Key Points */}
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Key Points</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addKeyPoint}>
                <Plus className="w-4 h-4 mr-1" />
                Add Point
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {formData.key_points.map((point, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={point}
                    onChange={(e) => handleKeyPointChange(index, e.target.value)}
                    placeholder={`Key point ${index + 1}`}
                    data-testid={`key-point-${index}`}
                  />
                  {formData.key_points.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeKeyPoint(index)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Discussion Questions */}
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Discussion Questions</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                <Plus className="w-4 h-4 mr-1" />
                Add Question
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {formData.discussion_questions.map((question, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={question}
                    onChange={(e) => handleQuestionChange(index, e.target.value)}
                    placeholder={`Discussion question ${index + 1}`}
                    data-testid={`discussion-question-${index}`}
                  />
                  {formData.discussion_questions.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeQuestion(index)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link to="/toolbox-talks">
              <Button type="button" variant="outline">Cancel</Button>
            </Link>
            <Button type="submit" disabled={saving} data-testid="save-talk-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Create Toolbox Talk
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default ToolboxTalkBuilder;
